import React, { useEffect, useState, useContext } from "react";
import "./Footer.css";
import {
  FaInstagram,
  FaFacebookF,
  FaYoutube,
  FaTiktok,
  FaLinkedin,
} from "react-icons/fa";
import axios from "axios";
import { LanguageContext } from "../../context/LanguageContext";
import { Link } from "react-router-dom";
import FooterLogo from "../../assets/images/footerLogo.png";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const getNestedValue = (obj, path) => {
  if (!obj || typeof obj !== "object" || !path) return "";

  const result = path.split(".").reduce((acc, key) => {
    if (acc && Object.prototype.hasOwnProperty.call(acc, key)) {
      return acc[key];
    }
    return undefined;
  }, obj);

  return typeof result === "string" ? result : "";
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { currentLang, texts } = useContext(LanguageContext);
  const t = (key) => getNestedValue(texts, key);

  const [footer, setFooter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [status, setStatus] = useState({ loading: false, success: "", error: "" });

  const parseYoutubeCombined = (str) => {
    const result = { youtube: "", tiktok: "", linkedin: "" };
    if (!str) return result;

    str.split(";").forEach((pair) => {
      const [key, val] = pair.split("=");
      if (result.hasOwnProperty(key?.trim())) {
        result[key.trim()] = val?.trim() || "";
      }
    });

    return result;
  };

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/Footer?lang=${currentLang}`);
        setFooter(res.data);
      } catch (err) {
        console.error("Failed to load footer:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchFooter();
  }, [currentLang]);

  const handleNewsletterSubmit = async () => {
    if (!newsletterEmail.trim()) return;

    setStatus({ loading: true, success: "", error: "" });

    try {
      await axios.post(`${API_BASE}/api/ContactUs/submit`, {
        fullName: "Newsletter Subscriber",
        email: newsletterEmail,
        phone: "",
        subject: "Newsletter",
        message: "User subscribed to newsletter.",
      });

      setStatus({
        loading: false,
        success: t("footer.subscribeSuccess"),
        error: "",
      });

      setNewsletterEmail("");
    } catch {
      setStatus({
        loading: false,
        success: "",
        error: t("footer.subscribeError"),
      });
    }
  };

  if (loading || !footer) return null;

  const socials = parseYoutubeCombined(footer.youtubeUrl);

  return (
    <footer className="footer">
      <div className="footer-top">

        <div className="footer-col">
          <h2 className="footer-title">{footer.socialTitle}</h2>
          <p className="footer-sub">{footer.socialSubtitle}</p>

          <div className="footer-icons">
            <a href={footer.facebookUrl} target="_blank" rel="noopener noreferrer">
              <FaFacebookF />
            </a>
            <a href={footer.instagramUrl} target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </a>
            {socials.youtube && (
              <a href={socials.youtube} target="_blank" rel="noopener noreferrer">
                <FaYoutube />
              </a>
            )}
            {socials.tiktok && (
              <a href={socials.tiktok} target="_blank" rel="noopener noreferrer">
                <FaTiktok />
              </a>
            )}
            {socials.linkedin && (
              <a href={socials.linkedin} target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
              </a>
            )}
          </div>
        </div>

        <div className="footer-col">
          <h2 className="footer-title">{footer.contactTitle}</h2>
          <p className="footer-sub">{t("footer.needHelp")}</p>

          <Link to="/contactUs" className="contact-button">
            {t("footer.contactUs")}
          </Link>
        </div>

        <div className="footer-col">
          <h2 className="footer-title">{footer.newsletterTitle}</h2>
          <p className="footer-sub newsletter-info">
            {t("footer.newsletterText")}
          </p>

          {status.success && <p className="newsletter-success">{status.success}</p>}
          {status.error && <p className="newsletter-error">{status.error}</p>}

          <div className="newsletter-row">
            <input
              type="email"
              placeholder={t("footer.emailPlaceholder")}
              className="newsletter-input"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
            />

            <button
              disabled={status.loading}
              onClick={handleNewsletterSubmit}
              className="newsletter-btn"
            >
              {t("footer.subscribe")}
            </button>
          </div>
        </div>

        <div className="footer-links">
          <Link to="/specification/MHERO1">{footer.specification} MHERO1</Link>
          <Link to="/specification/MHERO2">{footer.specification} MHERO2</Link>
          <Link to="/order">{t("footer.order")}</Link>
          <Link to="/testDrive">{t("footer.testDrive")}</Link>
        </div>

      </div>

      <hr />

      <div className="footer-copy">
        <p>© {currentYear} {footer.copyRight}</p>
        <a href="https://vuca.li/" target="_blank" rel="noopener noreferrer">
          <img src={FooterLogo} alt="Logo" className="footer-logo" />
        </a>
      </div>
    </footer>
  );
};

export default Footer;
